import React, { useMemo, useState } from "react";
import { VideoCameraOutlined } from "@ant-design/icons";
import { Button, Select, Space, Typography } from "antd";
import orderBy from "lodash.orderby";
import { useTranslation } from "react-i18next";

import { RequestStatus } from "@fitness-app/app-store";
import {
  MediaLibraryType,
  VideoUploadStatus,
  type MediaLibraryItem,
} from "@fitness-app/data-models/entities/MediaLibrary";

import { useAppSelector } from "~/store/initializeStore";

interface VideoLibraryPickerProps {
  onSelect: (video: MediaLibraryItem) => void;
}

const isPlayableVideo = (item: MediaLibraryItem) =>
  item.type === MediaLibraryType.Video && item.data.uploadStatus === VideoUploadStatus.Ready;

const formatDuration = (seconds: number | null | undefined) => {
  if (!seconds) {
    return null;
  }

  return `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, "0")}`;
};

/**
 * The body of the `video` EditorJS block: pick a video the media library already holds. Uploading a
 * new one lives next to the save button - a block that comes and goes is no place for it. Videos
 * still encoding are not offered; Bunny only serves the player once the upload reaches `Ready`.
 *
 * Which videos the list holds is decided by the screen's subscription, not here.
 */
const VideoLibraryPicker = ({ onSelect }: VideoLibraryPickerProps) => {
  const { t } = useTranslation(["products", "dashboard"]);
  const { list, listStatus } = useAppSelector((store) => store.mediaLibrary);
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);

  const readyVideos = useMemo(() => list.filter(isPlayableVideo), [list]);

  const options = useMemo(
    () =>
      orderBy(
        readyVideos.map((video) => ({ value: video.id, label: video.title })),
        "label",
      ),
    [readyVideos],
  );

  const handleInsert = () => {
    const video = readyVideos.find((item) => item.id === selectedVideoId);

    if (video) {
      onSelect(video);
    }
  };

  return (
    <Space direction="vertical" size={4} className="w-full py-2">
      <Space wrap>
        <Select
          showSearch
          allowClear
          style={{ minWidth: 320 }}
          // Uploads are named after the file, so the titles are long and alike - let the dropdown
          // grow past the input instead of cutting them off, and show what each one looks like.
          popupMatchSelectWidth={false}
          listHeight={320}
          value={selectedVideoId}
          onChange={(value: string | null) => setSelectedVideoId(value ?? null)}
          options={options}
          optionRender={(option) => {
            const video = readyVideos.find((item) => item.id === option.value);
            const duration = formatDuration(video?.data.length);

            return (
              <Space align="center">
                {video?.data.thumbnailUrl ? (
                  <img
                    alt=""
                    src={video.data.thumbnailUrl}
                    className="h-[45px] w-[80px] shrink-0 rounded object-cover"
                  />
                ) : (
                  <div className="flex h-[45px] w-[80px] shrink-0 items-center justify-center rounded bg-gray-100 text-gray-400">
                    <VideoCameraOutlined />
                  </div>
                )}
                <div className="flex flex-col">
                  <span className="whitespace-normal break-all">{option.label}</span>
                  {duration ? <span className="text-xs text-gray-400">{duration}</span> : null}
                </div>
              </Space>
            );
          }}
          loading={listStatus === RequestStatus.SUBSCRIBING}
          placeholder={t("productResources.form.selectVideo")}
          filterOption={(inputValue, option) => option?.label.toLowerCase().includes(inputValue.toLowerCase()) ?? false}
          notFoundContent={t("productResources.form.noReadyVideos")}
        />
        <Button type="primary" disabled={!selectedVideoId} onClick={handleInsert}>
          {t("productResources.form.insertVideo")}
        </Button>
      </Space>
      <Typography.Text type="secondary">{t("productResources.form.videoHint")}</Typography.Text>
    </Space>
  );
};

export default VideoLibraryPicker;
